import { createContext, ReactNode, useEffect, useState } from 'react';
import { User } from 'oidc-client-ts';

import authService from './auth.service';

export interface AuthContextType {
  token: undefined | string;
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  getLanguage: () => Promise<undefined | string>;
  impersonate: (token: string) => Promise<User>;
  signInRedirectCallback: () => Promise<void>;
  getToken: () => Promise<void>;
  logout: () => void;
  login: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<undefined | string>(undefined);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const login = async () => {
    setIsLoading(true);
    try {
      await authService.login(); // This will trigger a redirect
    } catch (error) {
      console.error('Error logging in:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const getAccessToken = async () => {
    return authService.getAccessToken();
  };

  const getLanguage = async () => {
    return authService.getLanguage();
  };

  const impersonate = async (token: string) => {
    return authService.impersonate(token);
  };

  const getToken = async () => {
    const token = await getAccessToken();
    setToken(token);
  };

  const signInRedirectCallback = async () => {
    await authService.signinRedirectCallback();
    await getToken();
  };

  const value = {
    isAuthenticated: !!user && !user.expired,
    isLoading,
    token,
    user,
    signInRedirectCallback,
    getLanguage,
    impersonate,
    getToken,
    logout,
    login,
  };

  useEffect(() => {
    const checkUser = async () => {
      try {
        const currentUser = await authService.getUser();
        if (currentUser) {
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Error checking user state:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkUser();
  }, []);

  useEffect(() => {
    getToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Listen for automatic token renewal
    const userLoadedHandler = () => {
      getToken();
    };

    // Subscribe to user loaded events (fired when token is silently renewed)
    authService.userManager.events.addUserLoaded(userLoadedHandler);

    return () => {
      authService.userManager.events.removeUserLoaded(userLoadedHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
